import type { UniverseSort } from './types';

/**
 * Discovery ordering. The backend applies the same rules to the whole authorized
 * result set before paginating (see README "Sorting"); the client only sorts
 * locally when it already holds the complete set.
 *
 * Name order: `en` collation, case- and accent-insensitive, numeric-aware
 * ("Deck 2" < "Deck 10"), whitespace collapsed for comparison only, missing
 * names last in both directions, canonical id as the stable tie-breaker.
 */
export const SORT_LOCALE = 'en';
export const DEFAULT_SORT = 'az' as const;
export const BROWSE_SORTS: readonly UniverseSort[] = ['az', 'za', 'newest', 'updated'];
export const SEARCH_SORTS: readonly UniverseSort[] = ['az', 'za', 'relevant', 'newest', 'updated'];

const collator = new Intl.Collator(SORT_LOCALE, { sensitivity: 'base', numeric: true, usage: 'sort' });

export function nameKey(title: string | null | undefined): string {
  return String(title ?? '').replace(/\s+/g, ' ').trim();
}

interface Sortable {
  id: string;
  title: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

function byId(a: Sortable, b: Sortable): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function compareByName(a: Sortable, b: Sortable, direction: 1 | -1 = 1): number {
  const ka = nameKey(a.title);
  const kb = nameKey(b.title);
  if (!ka || !kb) {
    if (ka !== kb) return ka ? -1 : 1;
    return byId(a, b);
  }
  return collator.compare(ka, kb) * direction || byId(a, b);
}

function time(v: string | null | undefined): number | null {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

/** Newest first; undated records last; then name A–Z so equal timestamps stay deterministic. */
function compareByTime(a: Sortable, b: Sortable, field: 'created_at' | 'updated_at'): number {
  const ta = time(a[field]);
  const tb = time(b[field]);
  if (ta === null || tb === null) {
    if (ta !== tb) return ta === null ? 1 : -1;
  } else if (ta !== tb) {
    return tb - ta;
  }
  return compareByName(a, b);
}

/** URL (shared link) first, then the saved choice, then Name: A–Z — each only if valid here. */
export function resolveSort(urlValue: string | null, saved: UniverseSort | null, allowed: readonly UniverseSort[]): UniverseSort {
  if (urlValue && allowed.includes(urlValue as UniverseSort)) return urlValue as UniverseSort;
  if (saved && allowed.includes(saved)) return saved;
  return allowed.includes(DEFAULT_SORT) ? DEFAULT_SORT : (allowed[0] ?? DEFAULT_SORT);
}

/** Returns a new array; 'relevant' keeps the server's ranking. */
export function sortAssets<T extends Sortable>(items: readonly T[], sort: UniverseSort): T[] {
  const copy = [...items];
  switch (sort) {
    case 'relevant':
      return copy;
    case 'az':
      return copy.sort((a, b) => compareByName(a, b, 1));
    case 'za':
      return copy.sort((a, b) => compareByName(a, b, -1));
    case 'newest':
      return copy.sort((a, b) => compareByTime(a, b, 'created_at'));
    case 'updated':
      return copy.sort((a, b) => compareByTime(a, b, 'updated_at'));
  }
}
